import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { productsAPI, customersAPI, salesAPI, sucursalesAPI, dashboardAPI, expensesAPI } from '../services/api';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getSucursalId, isBotica, isJuguteria } from '../hooks/useSucursal';
import { addPendingSale, searchCachedProducts, searchCachedCustomers } from '../db/localDB';
import { refreshOfflineCache } from '../db/cacheService';
import { registerBackgroundSync } from '../db/syncEngine';
import './SaleFormMobile.css';

/* ── Helpers ──────────────────────────────────────────────── */
const PILL_COLORS = [
    '#ef4444','#f97316','#eab308','#22c55e',
    '#3b82f6','#8b5cf6','#ec4899','#14b8a6','#64748b'
];
const getPillColor = (str = '') => {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return PILL_COLORS[Math.abs(h) % PILL_COLORS.length];
};
const fmt = (v) => `S/ ${parseFloat(v || 0).toFixed(2)}`;
const fmtHour = (isoStr) => {
    if (!isoStr) return '';
    try {
        return new Date(isoStr).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

const PAY_LABELS = { efectivo: '💵 Efectivo', yape: '📱 Yape', tarjeta: '💳 Tarjeta', fiado: '📝 Fiado' };

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL — SaleFormMobile
   Navegación por 3 tabs: Vender | Ventas | Resumen
═══════════════════════════════════════════════════════════ */
const SaleFormMobile = ({ onSaleSuccess }) => {

    /* ── Online ──────────────────────────────────────────── */
    const isOnline = useOnlineStatus();

    /* ── Navegación principal ────────────────────────────── */
    const [mainTab, setMainTab] = useState('pos'); // 'pos' | 'ventas' | 'resumen'

    /* ── POS — Estado del flujo ──────────────────────────── */
    const [step, setStep] = useState(1);

    /* ── POS — Productos ─────────────────────────────────── */
    const [products, setProducts]                 = useState([]);
    const [searchTerm, setSearchTerm]             = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [isSearching, setIsSearching]           = useState(false);

    /* ── POS — Carrito ───────────────────────────────────── */
    const [cart, setCart] = useState([]);

    /* ── POS — Clientes ──────────────────────────────────── */
    const [customers, setCustomers]                 = useState([]);
    const [customerSearch, setCustomerSearch]       = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer]   = useState(null);

    /* ── POS — Pago ──────────────────────────────────────── */
    const [paymentMethod, setPaymentMethod]   = useState('efectivo');
    const [amountReceived, setAmountReceived] = useState('');
    const [discountAmount, setDiscountAmount] = useState('');
    const [notes, setNotes]                   = useState('');

    /* ── Tab VENTAS ──────────────────────────────────────── */
    const [salesPeriod, setSalesPeriod]   = useState('today'); // 'today' | 'week' | 'month'
    const [todaySales, setTodaySales]     = useState([]);
    const [loadingSales, setLoadingSales] = useState(false);
    const [expandedSale, setExpandedSale] = useState(null);

    /* ── Tab RESUMEN ─────────────────────────────────────── */
    const [dashStats, setDashStats]         = useState(null);
    const [loadingStats, setLoadingStats]   = useState(false);
    const [dashPeriod, setDashPeriod]       = useState('today'); // 'today' | 'week' | 'month'
    const [expenses, setExpenses]           = useState([]);
    const [showExpenseForm, setShowExpenseForm] = useState(false);
    const [newExpense, setNewExpense]        = useState({ description: '', amount: '', category: 'general' });
    const [savingExpense, setSavingExpense]  = useState(false);

    /* ── UI Global ───────────────────────────────────────── */
    const [toast, setToast]         = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [saleNumber, setSaleNumber]   = useState(null);

    /* ── Refs ────────────────────────────────────────────── */
    const searchRef = useRef(null);
    const amountRef = useRef(null);

    /* ── User ────────────────────────────────────────────── */
    const user = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); }
        catch { return {}; }
    }, []);
    const userId    = user.id || null;
    const juguteria = isJuguteria();

    /* ── Toast ───────────────────────────────────────────── */
    const showToast = useCallback((message, type = 'error') => {
        setToast({ message, type, key: Date.now() });
        setTimeout(() => setToast(null), 3200);
    }, []);

    /* ══ DATA: PRODUCTOS ═════════════════════════════════ */
    const fetchProducts = useCallback(async () => {
        if (!isOnline) return;
        try {
            const sucursalId = getSucursalId();
            if (sucursalId) {
                const res = await sucursalesAPI.getProductos(sucursalId);
                setProducts((res.data?.data?.productos || []).map(p => ({
                    ...p,
                    id:         p.producto_id,
                    sale_price: p.precio_efectivo,
                    stock:      p.stock_sucursal !== undefined ? p.stock_sucursal : p.stock_general,
                })));
            } else {
                const res = await productsAPI.getAll();
                setProducts(res.data?.data?.products || []);
            }
        } catch (err) { console.error('Error cargando productos:', err); }
    }, [isOnline]);

    /* ══ DATA: CLIENTES ══════════════════════════════════ */
    const fetchCustomers = useCallback(async () => {
        if (!isOnline) return;
        try {
            const res = await customersAPI.getAll();
            setCustomers(res.data?.data?.customers || []);
        } catch (err) { console.error('Error cargando clientes:', err); }
    }, [isOnline]);

    /* ══ DATA: VENTAS (tab) ══════════════════════════════ */
    const fetchSales = useCallback(async (period = 'today') => {
        if (!isOnline) return;
        setLoadingSales(true);
        try {
            const periodMap = { today: 'today', week: 'week', month: 'month' };
            const res = await salesAPI.getAll({ period: periodMap[period], limit: 50 });
            const list = res.data?.data?.sales;
            setTodaySales(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Error cargando ventas:', err);
            setTodaySales([]);
        } finally {
            setLoadingSales(false);
        }
    }, [isOnline]);

    /* ══ DATA: RESUMEN (tab) ═════════════════════════════ */
    const fetchStats = useCallback(async (period = 'today') => {
        if (!isOnline) return;
        setLoadingStats(true);
        try {
            const res = await dashboardAPI.getStats({ period });
            setDashStats(res.data?.data || null);
        } catch (err) {
            console.error('Error cargando stats:', err);
            setDashStats(null);
        } finally {
            setLoadingStats(false);
        }
    }, [isOnline]);

    /* ══ DATA: GASTOS ════════════════════════════════════ */
    const fetchExpenses = useCallback(async () => {
        if (!isOnline) return;
        try {
            const today = new Date().toISOString().slice(0, 10);
            const res = await expensesAPI.getAll({ startDate: today, endDate: today });
            const list = res.data?.data?.expenses || res.data?.expenses || [];
            setExpenses(Array.isArray(list) ? list : []);
        } catch { setExpenses([]); }
    }, [isOnline]);

    /* ── Inicialización ──────────────────────────────────── */
    useEffect(() => {
        if (!isOnline) return;
        fetchProducts();
        fetchCustomers();
    }, [isOnline]);

    useEffect(() => {
        if (!isOnline || !userId) return;
        refreshOfflineCache(userId, getSucursalId());
        const cleanup = registerBackgroundSync(userId, (result) => {
            if (result.synced > 0) showToast(`✅ ${result.synced} venta(s) sincronizada(s)`, 'success');
        });
        return () => { if (typeof cleanup === 'function') cleanup(); };
    }, [isOnline, userId, showToast]);

    // Cargar datos según tab activa
    useEffect(() => {
        if (mainTab === 'ventas') fetchSales(salesPeriod);
        if (mainTab === 'resumen') { fetchStats(dashPeriod); fetchExpenses(); }
    }, [mainTab]);

    useEffect(() => {
        if (mainTab === 'ventas') fetchSales(salesPeriod);
    }, [salesPeriod]);

    useEffect(() => {
        if (mainTab === 'resumen') fetchStats(dashPeriod);
    }, [dashPeriod]);

    /* ══ BÚSQUEDA PRODUCTOS ══════════════════════════════ */
    const handleProductSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 2) { setFilteredProducts([]); setIsSearching(false); return; }
        setIsSearching(true);
        try {
            if (isOnline) {
                const t = term.toLowerCase();
                setFilteredProducts(products.filter(p =>
                    p.name?.toLowerCase().includes(t) ||
                    p.code?.toLowerCase().includes(t) ||
                    p.category?.toLowerCase().includes(t)
                ));
            } else {
                setFilteredProducts(await searchCachedProducts(term, userId));
            }
        } catch { /* silencioso */ } finally { setIsSearching(false); }
    }, [isOnline, products, userId]);

    useEffect(() => {
        const id = setTimeout(() => handleProductSearch(searchTerm), 250);
        return () => clearTimeout(id);
    }, [searchTerm, handleProductSearch]);

    /* ══ BÚSQUEDA CLIENTES ═══════════════════════════════ */
    const handleCustomerSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 1) { setFilteredCustomers([]); return; }
        if (isOnline) {
            setFilteredCustomers(customers.filter(c =>
                c.name?.toLowerCase().includes(term.toLowerCase()) ||
                c.document_number?.includes(term) ||
                c.phone?.includes(term)
            ));
        } else {
            setFilteredCustomers(await searchCachedCustomers(term, userId));
        }
    }, [isOnline, customers, userId]);

    useEffect(() => {
        const id = setTimeout(() => handleCustomerSearch(customerSearch), 250);
        return () => clearTimeout(id);
    }, [customerSearch, handleCustomerSearch]);

    /* ══ DISPLAY PRODUCTS ════════════════════════════════ */
    const displayProducts = useMemo(() => {
        if (searchTerm.length >= 2) return filteredProducts;
        return products.filter(p => p.is_active !== false).slice(0, 25);
    }, [products, filteredProducts, searchTerm]);

    /* ══ CARRITO ═════════════════════════════════════════ */
    const addToCart = useCallback((product) => {
        if (product.stock <= 0) { showToast(`❌ ${product.name} sin stock`); return; }
        const cartItemId = `${product.id}-base`;
        const existing = cart.find(i => i.cartItemId === cartItemId);
        if (existing) {
            if (existing.quantity >= product.stock) { showToast(`⚠️ Máximo: ${Math.floor(product.stock)}`); return; }
            setCart(prev => prev.map(i => i.cartItemId === cartItemId ? { ...i, quantity: i.quantity + 1 } : i));
        } else {
            setCart(prev => [...prev, { ...product, cartItemId, quantity: 1, current_price: product.sale_price, presentation_name: product.unit || 'Unidad', presentation_id: null, factor: 1 }]);
        }
        showToast(`✅ ${product.name}`, 'success');
    }, [cart, showToast]);

    const updateQty = (cartItemId, delta) => {
        setCart(prev => prev.map(i => {
            if (i.cartItemId !== cartItemId) return i;
            const newQty = Math.max(0, i.quantity + delta);
            if (newQty === 0) return null;
            if (newQty > i.stock) return i;
            return { ...i, quantity: newQty };
        }).filter(Boolean));
    };

    const removeFromCart = (cartItemId) => setCart(prev => prev.filter(i => i.cartItemId !== cartItemId));

    /* ══ TOTALES ═════════════════════════════════════════ */
    const subtotal   = useMemo(() => cart.reduce((s, i) => s + (parseFloat(i.current_price)||0)*(parseFloat(i.quantity)||0), 0), [cart]);
    const discount   = parseFloat(discountAmount) || 0;
    const total      = useMemo(() => { const a = Math.max(0, subtotal - discount); return Math.round((a+0.001)/0.10)*0.10; }, [subtotal, discount]);
    const vuelto     = useMemo(() => Math.max(0, (parseFloat(amountReceived)||0) - total), [amountReceived, total]);
    const totalItems = cart.reduce((s, i) => s + (parseFloat(i.quantity)||0), 0);

    /* ══ RESET POS ═══════════════════════════════════════ */
    const handleNuevaVenta = useCallback(() => {
        setCart([]); setSelectedCustomer(null); setCustomerSearch('');
        setPaymentMethod('efectivo'); setAmountReceived('');
        setDiscountAmount(''); setNotes('');
        setSearchTerm(''); setFilteredProducts([]); setStep(1);
    }, []);

    /* ══ SUBMIT VENTA ════════════════════════════════════ */
    const handleSubmit = async () => {
        if (cart.length === 0) { showToast('⚠️ El carrito está vacío', 'warning'); return; }
        if (paymentMethod === 'fiado' && !selectedCustomer) { showToast('❌ Selecciona un cliente para fiado', 'error'); return; }

        const customerName = selectedCustomer?.name || customerSearch.trim() || 'Cliente General';
        const discountPct  = subtotal > 0 && discount > 0 ? Math.min((discount/subtotal)*100, 100) : 0;

        const saleData = {
            customer_id:         selectedCustomer ? selectedCustomer.id : null,
            items: cart.map(item => ({
                product_id: item.id, quantity: parseFloat(item.quantity)||0,
                unit_price: parseFloat(item.current_price)||0,
                subtotal:   (parseFloat(item.quantity)||0)*(parseFloat(item.current_price)||0),
                factor: item.factor||1, presentation_name: item.presentation_name||'Unidad', presentation_id: item.presentation_id||null
            })),
            payment_method: paymentMethod, discount_percentage: discountPct,
            notes: notes.trim() || (!selectedCustomer ? `Cliente: ${customerName}` : ''),
            operation_type: 'venta_directa', sucursal_id: getSucursalId(),
        };

        if (!isOnline) {
            try {
                await addPendingSale(saleData, userId);
                showToast('💾 Venta guardada offline', 'info');
                setTimeout(handleNuevaVenta, 1800);
            } catch { showToast('❌ No se pudo guardar offline', 'error'); }
            return;
        }

        try {
            const response = await salesAPI.create(saleData);
            if (!response.data.success) throw new Error(response.data.message || 'Error');
            setSaleNumber(response.data.data.sale.correlativo_local || response.data.data.sale.id);
            setShowSuccess(true);
            if (onSaleSuccess) onSaleSuccess(response.data.data.sale);
            setTimeout(() => { setShowSuccess(false); handleNuevaVenta(); fetchProducts(); }, 1800);
        } catch (error) {
            showToast(`❌ ${error.response?.data?.message || error.message || 'Error'}`);
        }
    };

    /* ══ SUBMIT GASTO ════════════════════════════════════ */
    const handleSaveExpense = async () => {
        if (!newExpense.description.trim() || !newExpense.amount || parseFloat(newExpense.amount) <= 0) {
            showToast('⚠️ Completa descripción y monto', 'warning'); return;
        }
        setSavingExpense(true);
        try {
            await expensesAPI.create({
                description: newExpense.description.trim(),
                amount: parseFloat(newExpense.amount),
                category: newExpense.category,
            });
            setNewExpense({ description: '', amount: '', category: 'general' });
            setShowExpenseForm(false);
            showToast('✅ Gasto registrado', 'success');
            fetchExpenses();
            fetchStats(dashPeriod);
        } catch { showToast('❌ Error al guardar gasto'); }
        finally { setSavingExpense(false); }
    };

    /* ══ NAVIGATION ══════════════════════════════════════ */
    const goToStep = (s) => {
        if (s === 2 && cart.length === 0) { showToast('⚠️ Agrega al menos un producto', 'warning'); return; }
        if (s === 3 && cart.length === 0) { showToast('⚠️ El carrito está vacío', 'warning'); return; }
        setStep(s);
        if (s === 3) setTimeout(() => amountRef.current?.focus(), 200);
    };

    const switchTab = (tab) => { setMainTab(tab); if (tab === 'pos') setStep(1); };

    /* ── Colores de estado ───────────────────────────────── */
    const estadoColor = (s) => ({ 'COMPLETADA': '#10b981', 'ANULADA': '#ef4444', 'PENDIENTE': '#f59e0b' }[s] || '#6366f1');

    /* ══ RENDER ══════════════════════════════════════════ */
    const stepDefs = [{ label: 'Productos' }, { label: 'Carrito' }, { label: 'Cobrar' }];
    const payMethods = [
        { id: 'efectivo', icon: '💵', label: 'Efectivo' },
        { id: 'yape',     icon: '📱', label: 'Yape / Plin' },
        { id: 'tarjeta',  icon: '💳', label: 'Tarjeta' },
        { id: 'fiado',    icon: '📝', label: 'Fiado' },
    ];
    const periodLabels = { today: 'Hoy', week: 'Esta semana', month: 'Este mes' };

    const todaySalesSummary = useMemo(() => ({
        count: todaySales.length,
        total: todaySales.reduce((s, v) => s + parseFloat(v.total_amount||0), 0),
        canceladas: todaySales.filter(v => v.estado === 'ANULADA').length,
    }), [todaySales]);

    return (
        <div className="mpos-root">

            {/* ── Top Bar ──────────────────────────────── */}
            <div className="mpos-topbar">
                <div className="mpos-topbar-left">
                    <span className="mpos-topbar-title">
                        {juguteria ? '🧩 Juguetería' : '🏪 Punto de Venta'}
                    </span>
                    <span className="mpos-topbar-sub">
                        {user.name || 'Usuario'} · {new Date().toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })}
                    </span>
                </div>
                <div className="mpos-topbar-right">
                    <div className={`mpos-status-dot ${isOnline ? 'mpos-status-online' : 'mpos-status-offline'}`} title={isOnline ? 'En línea' : 'Sin conexión'} />
                    {mainTab === 'pos' && (
                        <button className="mpos-topbar-btn" onClick={handleNuevaVenta}>+ Nueva</button>
                    )}
                    {mainTab === 'ventas' && (
                        <button className="mpos-topbar-btn" onClick={() => fetchSales(salesPeriod)}>↺ Actualizar</button>
                    )}
                    {mainTab === 'resumen' && (
                        <button className="mpos-topbar-btn" onClick={() => { fetchStats(dashPeriod); fetchExpenses(); }}>↺ Actualizar</button>
                    )}
                </div>
            </div>

            {/* ── Offline banner ───────────────────────── */}
            {!isOnline && (
                <div className="mpos-offline-banner">
                    📵 Sin conexión — las ventas se guardan localmente
                </div>
            )}

            {/* ── Step Bar (solo en tab POS) ────────────── */}
            {mainTab === 'pos' && (
                <div className="mpos-steps">
                    {stepDefs.map((s, i) => {
                        const stepNum = i + 1;
                        const state   = stepNum < step ? 'done' : stepNum === step ? 'active' : '';
                        return (
                            <React.Fragment key={stepNum}>
                                <div className={`mpos-step ${state}`} onClick={() => stepNum <= step && setStep(stepNum)}>
                                    <div className="mpos-step-circle">{stepNum < step ? '✓' : stepNum}</div>
                                    <span className="mpos-step-label">{s.label}</span>
                                </div>
                                {i < stepDefs.length - 1 && <div className={`mpos-step-connector ${stepNum < step ? 'done' : ''}`} />}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: VENDER (POS)
            ══════════════════════════════════════════════════ */}
            {mainTab === 'pos' && (
                <>
                    {/* PASO 1 — PRODUCTOS */}
                    {step === 1 && (
                        <div className="mpos-content">
                            <div className="mpos-searchbox">
                                <span className="mpos-search-icon">🔍</span>
                                <input ref={searchRef} type="text" className="mpos-search-input"
                                    placeholder="Buscar por nombre o código..."
                                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus />
                                {searchTerm && <button className="mpos-search-clear" onClick={() => setSearchTerm('')}>✕</button>}
                            </div>

                            <div className="mpos-section-header">
                                {searchTerm.length >= 2 ? (isSearching ? 'Buscando...' : `${displayProducts.length} resultado(s)`) : 'Productos disponibles'}
                            </div>

                            <div className="mpos-prod-list">
                                {displayProducts.length === 0 ? (
                                    <div className="mpos-prod-empty">
                                        <span>{isSearching ? '🔍' : '📦'}</span>
                                        {isSearching ? 'Buscando...' : searchTerm.length >= 2 ? 'No se encontraron productos' : 'Cargando productos...'}
                                    </div>
                                ) : displayProducts.map(product => {
                                    const stockOut = product.stock <= 0;
                                    const stockLow = !stockOut && product.stock <= (product.min_stock || 5);
                                    const inCart   = cart.find(i => i.cartItemId === `${product.id}-base`);
                                    return (
                                        <div key={product.id} className={`mpos-prod-card${stockOut ? ' agotado' : ''}`}>
                                            <div className="mpos-prod-avatar" style={{ background: getPillColor(product.name) }}>
                                                {juguteria ? '🧩' : (product.name||'?')[0].toUpperCase()}
                                            </div>
                                            <div className="mpos-prod-info">
                                                <span className="mpos-prod-name">{product.name}</span>
                                                <div className="mpos-prod-meta">
                                                    <span className={`mpos-stock-badge ${stockOut ? 'mpos-stock-out' : stockLow ? 'mpos-stock-low' : 'mpos-stock-ok'}`}>
                                                        {stockOut ? 'AGOTADO' : `Stock: ${Math.floor(product.stock)}`}
                                                    </span>
                                                    {product.category && <span className="mpos-cat-tag">{product.category}</span>}
                                                </div>
                                            </div>
                                            <div className="mpos-prod-price-col">
                                                <span className="mpos-prod-price">{fmt(product.sale_price)}</span>
                                                <button className="mpos-add-btn" disabled={stockOut} onClick={() => addToCart(product)}>
                                                    {inCart ? `+${inCart.quantity}` : '+'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* PASO 2 — CARRITO */}
                    {step === 2 && (
                        <div className="mpos-content">
                            {cart.length === 0 ? (
                                <div className="mpos-cart-empty">
                                    <span>🛒</span><p>El carrito está vacío</p>
                                    <small>Regresa al paso anterior para agregar productos</small>
                                </div>
                            ) : (
                                <>
                                    <div className="mpos-section-header">Items en el carrito</div>
                                    <div className="mpos-cart-list">
                                        {cart.map(item => {
                                            const sub = (parseFloat(item.current_price)||0)*(parseFloat(item.quantity)||0);
                                            return (
                                                <div key={item.cartItemId} className="mpos-cart-item">
                                                    <div className="mpos-cart-item-header">
                                                        <div className="mpos-cart-avatar" style={{ background: getPillColor(item.name) }}>
                                                            {juguteria ? '🧩' : (item.name||'?')[0].toUpperCase()}
                                                        </div>
                                                        <span className="mpos-cart-name">{item.name}</span>
                                                        <button className="mpos-cart-remove" onClick={() => removeFromCart(item.cartItemId)}>🗑</button>
                                                    </div>
                                                    <div className="mpos-cart-controls">
                                                        <div className="mpos-qty-control">
                                                            <button className="mpos-qty-btn" onClick={() => updateQty(item.cartItemId, -1)}>−</button>
                                                            <input className="mpos-qty-input" type="number" value={item.quantity}
                                                                onChange={e => {
                                                                    const v = parseInt(e.target.value)||0;
                                                                    if (v <= 0) { removeFromCart(item.cartItemId); return; }
                                                                    if (v > item.stock) return;
                                                                    setCart(prev => prev.map(i => i.cartItemId === item.cartItemId ? { ...i, quantity: v } : i));
                                                                }} />
                                                            <button className="mpos-qty-btn" onClick={() => updateQty(item.cartItemId, 1)}>+</button>
                                                        </div>
                                                        <span className="mpos-cart-subtotal">{fmt(sub)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Descuento */}
                                    <div className="mpos-discount-row">
                                        <span className="mpos-discount-label">🏷️ Descuento (opcional)</span>
                                        <div className="mpos-discount-input-wrap">
                                            <span className="mpos-discount-prefix">S/</span>
                                            <input className="mpos-discount-input" type="number" min="0" step="0.10"
                                                placeholder="0.00" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)} />
                                        </div>
                                    </div>

                                    {/* Cliente */}
                                    <div className="mpos-customer-section">
                                        <div className="mpos-section-title">👤 Cliente (opcional — requerido para Fiado)</div>
                                        {selectedCustomer ? (
                                            <div className="mpos-customer-selected">
                                                <span>👤</span>
                                                <span className="mpos-customer-selected-name">{selectedCustomer.name}</span>
                                                <button className="mpos-customer-clear" onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}>✕</button>
                                            </div>
                                        ) : (
                                            <>
                                                <input className="mpos-customer-search" type="text"
                                                    placeholder="Buscar cliente por nombre o DNI..."
                                                    value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
                                                {filteredCustomers.length > 0 && (
                                                    <div className="mpos-customer-results">
                                                        {filteredCustomers.slice(0, 5).map(c => (
                                                            <div key={c.id} className="mpos-customer-item"
                                                                onClick={() => { setSelectedCustomer(c); setFilteredCustomers([]); setCustomerSearch(''); }}>
                                                                <span>👤</span>
                                                                <div>
                                                                    <div className="mpos-customer-item-name">{c.name}</div>
                                                                    <div className="mpos-customer-item-doc">{c.document_number || c.phone || ''}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Totales */}
                                    <div className="mpos-cart-total-box">
                                        <div className="mpos-total-row"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                                        {discount > 0 && <div className="mpos-total-row"><span>Descuento</span><span style={{ color: '#ef4444' }}>− {fmt(discount)}</span></div>}
                                        <div className="mpos-total-row final"><span>TOTAL</span><span>{fmt(total)}</span></div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* PASO 3 — COBRAR */}
                    {step === 3 && (
                        <div className="mpos-content">
                            <div className="mpos-cobro-total">
                                <div className="mpos-cobro-total-label">Total a cobrar</div>
                                <div className="mpos-cobro-total-amount">{fmt(total)}</div>
                                {selectedCustomer && (
                                    <div style={{ fontSize: '0.8rem', color: '#6366f1', marginTop: 6, fontWeight: 600 }}>
                                        👤 {selectedCustomer.name}
                                    </div>
                                )}
                            </div>

                            <div className="mpos-section-header">Método de pago</div>
                            <div className="mpos-pay-methods">
                                {payMethods.map(m => (
                                    <button key={m.id} className={`mpos-pay-btn ${paymentMethod === m.id ? 'selected' : ''}`}
                                        onClick={() => setPaymentMethod(m.id)}>
                                        <span className="mpos-pay-btn-icon">{m.icon}</span>
                                        <span className="mpos-pay-btn-label">{m.label}</span>
                                    </button>
                                ))}
                            </div>

                            {paymentMethod === 'efectivo' && (
                                <div className="mpos-efectivo-section">
                                    <span className="mpos-efectivo-label">💵 Monto recibido del cliente</span>
                                    <input ref={amountRef} className="mpos-amount-input" type="number"
                                        min="0" step="0.10" placeholder={fmt(total)}
                                        value={amountReceived} onChange={e => setAmountReceived(e.target.value)} inputMode="decimal" />
                                    {amountReceived && parseFloat(amountReceived) >= total && (
                                        <div className="mpos-vuelto-row">
                                            <span className="mpos-vuelto-label">Vuelto</span>
                                            <span className="mpos-vuelto-amount">{fmt(vuelto)}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {paymentMethod === 'fiado' && !selectedCustomer && (
                                <div className="mpos-offline-banner">
                                    ⚠️ Para fiado debes seleccionar un cliente en el paso anterior
                                </div>
                            )}

                            <div className="mpos-section-header">Notas (opcional)</div>
                            <textarea className="mpos-notes-input" rows={2}
                                placeholder="Observaciones, referencias, etc."
                                value={notes} onChange={e => setNotes(e.target.value)} />
                        </div>
                    )}

                    {/* Bottom Bar POS */}
                    <div className="mpos-step-actions">
                        {step > 1 && (
                            <button className="mpos-bottom-btn mpos-bottom-btn--prev" onClick={() => setStep(s => s - 1)}>← Atrás</button>
                        )}
                        <div className="mpos-bottom-info">
                            <span className="mpos-bottom-items">{totalItems > 0 ? `${totalItems} item(s)` : 'Carrito vacío'}</span>
                            <span className="mpos-bottom-total">{fmt(total)}</span>
                        </div>
                        {step === 1 && (
                            <button className="mpos-bottom-btn mpos-bottom-btn--primary" disabled={cart.length === 0} onClick={() => goToStep(2)}>
                                Ver carrito ({totalItems}) →
                            </button>
                        )}
                        {step === 2 && (
                            <button className="mpos-bottom-btn mpos-bottom-btn--primary" disabled={cart.length === 0} onClick={() => goToStep(3)}>
                                Cobrar →
                            </button>
                        )}
                        {step === 3 && (
                            <button className="mpos-bottom-btn mpos-bottom-btn--success"
                                disabled={cart.length === 0 || (paymentMethod === 'fiado' && !selectedCustomer)}
                                onClick={handleSubmit}>
                                ✅ Registrar Venta
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: VENTAS — Historial completo
            ══════════════════════════════════════════════════ */}
            {mainTab === 'ventas' && (
                <>
                    <div className="mpos-content">
                        {/* Filtro de período */}
                        <div className="mpos-period-pills">
                            {['today','week','month'].map(p => (
                                <button key={p} className={`mpos-period-pill ${salesPeriod === p ? 'active' : ''}`}
                                    onClick={() => setSalesPeriod(p)}>
                                    {periodLabels[p]}
                                </button>
                            ))}
                        </div>

                        {/* Resumen del período */}
                        {!loadingSales && todaySales.length > 0 && (
                            <div className="mpos-ventas-summary">
                                <div className="mpos-ventas-summary-item">
                                    <span className="mpos-ventas-summary-val">{todaySalesSummary.count}</span>
                                    <span className="mpos-ventas-summary-lbl">Ventas</span>
                                </div>
                                <div className="mpos-ventas-summary-divider" />
                                <div className="mpos-ventas-summary-item">
                                    <span className="mpos-ventas-summary-val">{fmt(todaySalesSummary.total)}</span>
                                    <span className="mpos-ventas-summary-lbl">Total</span>
                                </div>
                                {todaySalesSummary.canceladas > 0 && (
                                    <>
                                        <div className="mpos-ventas-summary-divider" />
                                        <div className="mpos-ventas-summary-item">
                                            <span className="mpos-ventas-summary-val" style={{ color: '#ef4444' }}>{todaySalesSummary.canceladas}</span>
                                            <span className="mpos-ventas-summary-lbl">Anuladas</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Lista de ventas */}
                        {loadingSales ? (
                            <div className="mpos-loading-state">
                                <div className="mpos-loading-spin" />
                                <span>Cargando ventas...</span>
                            </div>
                        ) : todaySales.length === 0 ? (
                            <div className="mpos-prod-empty">
                                <span>📭</span>
                                No hay ventas registradas para {periodLabels[salesPeriod].toLowerCase()}
                            </div>
                        ) : (
                            <div className="mpos-sale-list">
                                {todaySales.map((sale, i) => {
                                    const isExpanded = expandedSale === sale.id;
                                    const anulada = sale.estado === 'ANULADA';
                                    return (
                                        <div key={sale.id} className={`mpos-sale-card ${anulada ? 'anulada' : ''}`}>
                                            <div className="mpos-sale-card-header" onClick={() => setExpandedSale(isExpanded ? null : sale.id)}>
                                                <div className="mpos-sale-num" style={{ background: anulada ? '#fef2f2' : 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
                                                    <span style={{ color: anulada ? '#ef4444' : '#fff', fontSize: '0.68rem', fontWeight: 800 }}>
                                                        #{sale.correlativo_local || sale.id}
                                                    </span>
                                                </div>
                                                <div className="mpos-sale-info">
                                                    <div className="mpos-sale-client">{sale.customer_name || 'Cliente General'}</div>
                                                    <div className="mpos-sale-meta">
                                                        <span>{PAY_LABELS[sale.payment_method] || sale.payment_method}</span>
                                                        {sale.created_at && <span>· {fmtHour(sale.created_at)}</span>}
                                                        {anulada && <span className="mpos-sale-anulada-badge">ANULADA</span>}
                                                    </div>
                                                </div>
                                                <div className="mpos-sale-amount-col">
                                                    <span className={`mpos-sale-amount ${anulada ? 'anulada-text' : ''}`}>
                                                        {fmt(sale.total_amount)}
                                                    </span>
                                                    <span className="mpos-sale-chevron">{isExpanded ? '▲' : '▼'}</span>
                                                </div>
                                            </div>
                                            {isExpanded && (
                                                <div className="mpos-sale-detail">
                                                    <div className="mpos-sale-detail-row">
                                                        <span>Estado</span>
                                                        <span style={{ color: estadoColor(sale.estado), fontWeight: 700 }}>{sale.estado || 'COMPLETADA'}</span>
                                                    </div>
                                                    {sale.discount_percentage > 0 && (
                                                        <div className="mpos-sale-detail-row">
                                                            <span>Descuento</span>
                                                            <span>{parseFloat(sale.discount_percentage).toFixed(1)}%</span>
                                                        </div>
                                                    )}
                                                    {sale.sucursal_nombre && (
                                                        <div className="mpos-sale-detail-row">
                                                            <span>Sucursal</span>
                                                            <span>{sale.sucursal_nombre}</span>
                                                        </div>
                                                    )}
                                                    {sale.notes && (
                                                        <div className="mpos-sale-detail-row">
                                                            <span>Notas</span>
                                                            <span style={{ fontStyle: 'italic' }}>{sale.notes}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className="mpos-step-actions mpos-step-actions--empty" />
                </>
            )}

            {/* ══════════════════════════════════════════════════
                TAB: RESUMEN — Dashboard mini
            ══════════════════════════════════════════════════ */}
            {mainTab === 'resumen' && (
                <>
                    <div className="mpos-content">
                        {/* Filtro de período */}
                        <div className="mpos-period-pills">
                            {['today','week','month'].map(p => (
                                <button key={p} className={`mpos-period-pill ${dashPeriod === p ? 'active' : ''}`}
                                    onClick={() => setDashPeriod(p)}>
                                    {periodLabels[p]}
                                </button>
                            ))}
                        </div>

                        {loadingStats ? (
                            <div className="mpos-loading-state">
                                <div className="mpos-loading-spin" />
                                <span>Cargando resumen...</span>
                            </div>
                        ) : dashStats ? (
                            <>
                                {/* Ventas de HOY siempre visible */}
                                <div className="mpos-section-header">📊 Ventas de hoy</div>
                                <div className="mpos-stat-grid">
                                    <div className="mpos-stat-card mpos-stat-card--blue">
                                        <span className="mpos-stat-val">{dashStats.today?.sales ?? '—'}</span>
                                        <span className="mpos-stat-lbl">Ventas</span>
                                    </div>
                                    <div className="mpos-stat-card mpos-stat-card--green">
                                        <span className="mpos-stat-val" style={{ fontSize: dashStats.today?.revenue > 9999 ? '1.05rem' : '1.3rem' }}>
                                            {fmt(dashStats.today?.revenue)}
                                        </span>
                                        <span className="mpos-stat-lbl">Ingresos</span>
                                    </div>
                                    <div className="mpos-stat-card mpos-stat-card--red">
                                        <span className="mpos-stat-val" style={{ fontSize: dashStats.today?.expenses > 9999 ? '1.05rem' : '1.3rem' }}>
                                            {fmt(dashStats.today?.expenses)}
                                        </span>
                                        <span className="mpos-stat-lbl">Gastos</span>
                                    </div>
                                    <div className={`mpos-stat-card ${(dashStats.today?.profit ?? 0) >= 0 ? 'mpos-stat-card--purple' : 'mpos-stat-card--red'}`}>
                                        <span className="mpos-stat-val" style={{ fontSize: Math.abs(dashStats.today?.profit) > 9999 ? '1.05rem' : '1.3rem' }}>
                                            {fmt(dashStats.today?.profit)}
                                        </span>
                                        <span className="mpos-stat-lbl">Ganancia neta</span>
                                    </div>
                                </div>

                                {/* Período seleccionado */}
                                {dashPeriod !== 'today' && (
                                    <>
                                        <div className="mpos-section-header">📈 {periodLabels[dashPeriod]}</div>
                                        <div className="mpos-stat-grid">
                                            <div className="mpos-stat-card mpos-stat-card--blue">
                                                <span className="mpos-stat-val">{dashStats.period?.sales ?? '—'}</span>
                                                <span className="mpos-stat-lbl">Ventas</span>
                                            </div>
                                            <div className="mpos-stat-card mpos-stat-card--green">
                                                <span className="mpos-stat-val" style={{ fontSize: dashStats.period?.revenue > 9999 ? '1.05rem' : '1.3rem' }}>
                                                    {fmt(dashStats.period?.revenue)}
                                                </span>
                                                <span className="mpos-stat-lbl">Ingresos</span>
                                            </div>
                                            <div className="mpos-stat-card mpos-stat-card--red">
                                                <span className="mpos-stat-val" style={{ fontSize: dashStats.period?.expenses > 9999 ? '1.05rem' : '1.3rem' }}>
                                                    {fmt(dashStats.period?.expenses)}
                                                </span>
                                                <span className="mpos-stat-lbl">Gastos</span>
                                            </div>
                                            <div className={`mpos-stat-card ${(dashStats.period?.profit ?? 0) >= 0 ? 'mpos-stat-card--purple' : 'mpos-stat-card--red'}`}>
                                                <span className="mpos-stat-val" style={{ fontSize: Math.abs(dashStats.period?.profit) > 9999 ? '1.05rem' : '1.3rem' }}>
                                                    {fmt(dashStats.period?.profit)}
                                                </span>
                                                <span className="mpos-stat-lbl">Ganancia neta</span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Inventario */}
                                <div className="mpos-section-header">📦 Inventario</div>
                                <div className="mpos-stat-grid mpos-stat-grid--2">
                                    <div className="mpos-stat-card mpos-stat-card--warn">
                                        <span className="mpos-stat-val">{dashStats.inventory?.lowStock ?? '—'}</span>
                                        <span className="mpos-stat-lbl">Stock bajo</span>
                                    </div>
                                    <div className="mpos-stat-card mpos-stat-card--red">
                                        <span className="mpos-stat-val">{dashStats.inventory?.outOfStock ?? '—'}</span>
                                        <span className="mpos-stat-lbl">Sin stock</span>
                                    </div>
                                </div>

                                {/* Top productos */}
                                {dashStats.topProducts?.length > 0 && (
                                    <>
                                        <div className="mpos-section-header">🏆 Top productos</div>
                                        <div className="mpos-top-list">
                                            {dashStats.topProducts.slice(0, 5).map((p, i) => (
                                                <div key={p.product_id || i} className="mpos-top-item">
                                                    <span className="mpos-top-rank">{i+1}</span>
                                                    <span className="mpos-top-name">{p.name || p.product_name}</span>
                                                    <span className="mpos-top-qty">{parseFloat(p.total_quantity||0).toFixed(0)} und</span>
                                                    <span className="mpos-top-amt">{fmt(p.total_sales)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="mpos-prod-empty">
                                <span>📊</span>No se pudo cargar el resumen
                            </div>
                        )}

                        {/* ── Gastos de hoy ── */}
                        <div className="mpos-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>💸 Gastos registrados hoy</span>
                            <button className="mpos-expense-add-btn" onClick={() => setShowExpenseForm(v => !v)}>
                                {showExpenseForm ? '✕ Cancelar' : '+ Agregar'}
                            </button>
                        </div>

                        {showExpenseForm && (
                            <div className="mpos-expense-form">
                                <input className="mpos-customer-search" type="text" placeholder="Descripción del gasto *"
                                    value={newExpense.description} onChange={e => setNewExpense(v => ({ ...v, description: e.target.value }))} />
                                <div className="mpos-discount-input-wrap" style={{ marginTop: 8 }}>
                                    <span className="mpos-discount-prefix">S/</span>
                                    <input className="mpos-discount-input" type="number" min="0" step="0.10"
                                        placeholder="Monto *" inputMode="decimal"
                                        value={newExpense.amount} onChange={e => setNewExpense(v => ({ ...v, amount: e.target.value }))} />
                                </div>
                                <select className="mpos-customer-search" style={{ marginTop: 8 }}
                                    value={newExpense.category} onChange={e => setNewExpense(v => ({ ...v, category: e.target.value }))}>
                                    <option value="general">General</option>
                                    <option value="limpieza">Limpieza</option>
                                    <option value="servicios">Servicios (luz, agua)</option>
                                    <option value="transporte">Transporte</option>
                                    <option value="personal">Personal</option>
                                    <option value="otros">Otros</option>
                                </select>
                                <button className="mpos-bottom-btn mpos-bottom-btn--success" style={{ width: '100%', marginTop: 10 }}
                                    onClick={handleSaveExpense} disabled={savingExpense}>
                                    {savingExpense ? 'Guardando...' : '💾 Guardar Gasto'}
                                </button>
                            </div>
                        )}

                        {expenses.length > 0 && (
                            <div className="mpos-expense-list">
                                {expenses.map((exp, i) => (
                                    <div key={exp.id || i} className="mpos-expense-item">
                                        <span className="mpos-expense-icon">💸</span>
                                        <div className="mpos-expense-info">
                                            <div className="mpos-expense-desc">{exp.description}</div>
                                            <div className="mpos-expense-cat">{exp.category || 'General'}</div>
                                        </div>
                                        <span className="mpos-expense-amount">{fmt(exp.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {expenses.length === 0 && !showExpenseForm && (
                            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem', padding: '12px 0 20px' }}>
                                Sin gastos registrados hoy
                            </div>
                        )}
                    </div>
                    <div className="mpos-step-actions mpos-step-actions--empty" />
                </>
            )}

            {/* ── Navegación principal (tabs) ───────────────── */}
            <div className="mpos-main-nav">
                <button className={`mpos-main-nav-item ${mainTab === 'pos' ? 'active' : ''}`} onClick={() => switchTab('pos')}>
                    <span className="mpos-main-nav-icon">{cart.length > 0 ? `🛒` : '🛍️'}</span>
                    <span className="mpos-main-nav-label">Vender</span>
                    {cart.length > 0 && <span className="mpos-cart-badge">{totalItems > 99 ? '99+' : totalItems}</span>}
                </button>
                <button className={`mpos-main-nav-item ${mainTab === 'ventas' ? 'active' : ''}`} onClick={() => switchTab('ventas')}>
                    <span className="mpos-main-nav-icon">📋</span>
                    <span className="mpos-main-nav-label">Ventas</span>
                </button>
                <button className={`mpos-main-nav-item ${mainTab === 'resumen' ? 'active' : ''}`} onClick={() => switchTab('resumen')}>
                    <span className="mpos-main-nav-icon">📊</span>
                    <span className="mpos-main-nav-label">Resumen</span>
                </button>
            </div>

            {/* ── Toast ────────────────────────────────────── */}
            {toast && (
                <div key={toast.key} className={`mpos-toast ${toast.type}`}>{toast.message}</div>
            )}

            {/* ── Success Modal ─────────────────────────────── */}
            {showSuccess && (
                <div className="mpos-success-overlay">
                    <div className="mpos-success-card">
                        <div className="mpos-success-icon">✅</div>
                        <div className="mpos-success-title">¡Venta Registrada!</div>
                        <div className="mpos-success-sub">
                            {saleNumber ? `Venta #${saleNumber}` : 'Venta procesada correctamente'}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SaleFormMobile;
